import {LitElement, html, css} from 'lit';
import {property} from 'lit/decorators/property.js';
import {customElement} from 'lit/decorators/custom-element.js';
import {connect} from 'pwa-helpers';
import {classMap} from 'lit/directives/class-map.js';
import {map} from 'lit/directives/map.js';
import {when} from 'lit/directives/when.js';
import {range} from 'lit/directives/range.js';
import {msg} from '@lit/localize';
import {
	AttributeType,
	ChoiceValueDecisionState,
} from '@viamedici-spc/configurator-ts';
import store from '../../store';
import {
	setOlivioHeadAngle,
	setOlivioHeadCap,
	setOlivioHeadColourTemperature,
	setOlivioHeadGoboAperture,
	setOlivioHeadHeight,
	setOlivioHeadHousingSize,
	setOlivioHeadLightDistribution,
	setOlivioHeadsData,
	setOlivioHeadType,
	setSelectedHead
} from '../olivioSlice';
import {
	generateOlivioHeadAngleSelector,
	generateOlivioHeadColourTemperatureSelector,
	generateOlivioHeadGoboApertureSelector,
	generateOlivioHeadHeightSelector,
	generateOlivioHeadHousingSizeSelector,
	generateOlivioHeadIsDifferentFromBase,
	generateOlivioHeadLightDistributionSelector,
	generateOlivioHeadTypeSelector,
	selectHeadTableVisible, selectOlivioAccessories, selectOlivioAccessoriesInclCustom, selectOlivioBracket,
	selectOlivioColourTemperatureInclCustom,
	selectOlivioDesignIsBollard,
	selectOlivioDesignIsCandelabra,
	selectOlivioDesignIsFloracion,
	selectOlivioDesignIsSistema,
	selectOlivioHeads,
	selectOlivioHousingSizeInclCustom,
	selectOlivioLightDistributionInclCustom,
	selectOlivioNumberOfHeads,
	selectOlivioOffsetFromBottom,
	selectOlivioOffsetFromTop,
	selectOlivioPoleHeight, selectOlivioReflectorColour,
	selectOlivioSelectedHead
} from '../olivioSelectors';
import {
	olivioHeadColourTemperatureOptions,
	olivioHeadGoboApertureOptions,
	olivioHeadHousingSizeOptions,
	olivioHeadTypeOptions
} from '../olivioModel';
import {selectSpcSession, spcValueSelectorFactory} from '../../selectors';
import {stringToAttributeId} from '../../components/spc/util/spcUtils';
import {spcExpliciteChoiceDecision} from '../../util/values';

@customElement('olivio-head-tabs')
export class OlivioHeadTabs extends connect(store)(LitElement) {

	@property({type: Object})
	title = {
		de: '[Titel]',
		en: '[Title]',
		fr: '[Title]'
	};

	static styles = [
		css`
			:host {
				width: var(--assistant-widget-width);
				display: flex;
				flex-direction: column;
				gap: 10px;
			}

			.tabs {
				display: flex;
				flex-direction: row;
				padding: 10px 0 10px;
				gap: 10px;
				justify-content: left;
				align-items: center;
			}

			.title {
				font: var(--font-widget-title);
				color: var(--color-widget-text);
				margin-bottom: 12px;
			}

			.info {
				font: var(--font-widget-label);
				color: var(--color-widget-text);
			}

			.box {
				background-color: var(--color-widget-background);
				padding: 12px;
				display: flex;
				break-inside: avoid;
				flex-flow: column;
				place-content: flex-start;
				gap: var(--large-space);
			}

			button {
				width: 24px;
				height: 24px;
				border-radius: 50%;
				padding: 0;
				font: var(--font-widget-label);
				color: var(--color-widget-border);
				background-color: transparent;
				border: 1px solid var(--color-widget-border, white);
				cursor: pointer;
				display: flex;
				align-items: center;
				justify-content: center;
				transition: color 0.4s ease, background-color 0.4s ease;
			}

			button:hover {
				background-color: var(--color-widget-background);
			}

			button.collision {
				border-color: var(--color-warn);
				color: var(--color-warn);
			}

			button.selected {
				width: 40px;
				height: 40px;
				color: var(--color-black);
				background-color: var(--color-white);
			}

			button.different {
				color: var(--color-black);
				background-color: var(--color-widget-border);
			}

			button.selected.collision {
				background-color: var(--color-warn);
				border-color: var(--color-warn);
			}

			button.selected.different {
				color: var(--color-black);
				background-color: var(--color-white);
			}

			.box {
				background-color: var(--color-widget-background);
				padding: 12px;
			}

			.settings {
				flex-direction: column;
				gap: 8px;
				display: none;
			}

			.settings.active {
				display: flex;
			}

		`];

	customOption = {
		id: 'custom',
		label: {
			de: 'Mixed',
			en: 'mixed',
			fr: 'mixed',
		},
		description: {
			de: '',
			en: '',
			fr: '',
		}
	};

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	async connectedCallback() {
		super.connectedCallback();
		addEventListener('keydown', this.handleKeydown);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		removeEventListener('keydown', this.handleKeydown);
	}

	stateChanged(state) {
		this.state = state;
		this.session = selectSpcSession(state);
		this.numberOfHeads = selectOlivioNumberOfHeads(state);
		this.selectedHead = selectOlivioSelectedHead(state);
		this.heads = selectOlivioHeads(state);
		this.maxHeadHeight = selectOlivioPoleHeight(state) - selectOlivioOffsetFromTop(state);
		this.minHeadHeight = selectOlivioOffsetFromBottom(state);
	}

	handleKeydown = (e) => {
		if (selectHeadTableVisible(this.state)) return; // do not use key commands when head table is active

		switch (e.keyCode) {
			case 38: // up
				this.incHeadHeight(this.selectedHead, e.shiftKey ? 0.1 : 0.01);
				break;
			case 40: // down
				this.incHeadHeight(this.selectedHead, e.shiftKey ? -0.1 : -0.01);
				break;
			case 39: // right
				this.incHeadAngle(this.selectedHead, e.shiftKey ? 10 : 1);
				break;
			case 37: // left
				this.incHeadAngle(this.selectedHead, e.shiftKey ? -10 : -1);
				break;
			case 48: // 0
				this.setSelected(null);
				break;
			case 49: // 1
				this.setSelected(0);
				break;
			case 50: // 2
				this.setSelected(1);
				break;
			case 51: // 3
				this.setSelected(2);
				break;
			case 52: // 4
				this.setSelected(3);
				break;
			case 53: // 5
				this.setSelected(4);
				break;
			case 54: // 6
				this.setSelected(5);
				break;
			case 55: // 7
				this.setSelected(6);
				break;
			case 56: // 8
				this.setSelected(7);
				break;
		}
	};

	incHeadHeight(instance, inc) {
		if (this.selectedHead === null) return;
		const selectHeight = generateOlivioHeadHeightSelector(instance);
		const value = Math.min(Math.max(selectHeight(this.state) + inc, this.minHeadHeight), this.maxHeadHeight);
		store.dispatch(setOlivioHeadHeight({instance: instance, value: value}));
	}

	incHeadAngle(instance, inc) {
		if (this.selectedHead === null) return;
		const selectAngle = generateOlivioHeadAngleSelector(instance);
		const value = (selectAngle(this.state) + inc) % 360;
		store.dispatch(setOlivioHeadAngle({instance: instance, value: value}));
	}

	hasCollisions(i) {
		return this.heads && this.heads[i]?.collisions?.length > 0;
	}

	setSelected(i) {
		store.dispatch(setSelectedHead({selectedHead: i}));
	}

	housingSizeCanBeSelected(state, id) {
		return (id !== 'custom')
			&&
			!(selectOlivioDesignIsCandelabra(state) && id === 'OlivioPiccolo') && // no piccolo for candelabra
			!(selectOlivioDesignIsSistema(state) && (selectOlivioBracket(state) === 'sistema1' || selectOlivioBracket(state) === 'sistema2') && id === 'OlivioPiccolo'); // no piccolo for sistema1/2
	}

	typeCanBeSelected(state, id, housingSize) {
		return (
			housingSize === 'OlivioGrande' && (id === 'luminaire' || id === 'gobo' || id === 'camera' || id === 'speaker') ||
			housingSize === 'OlivioMedio' && (id === 'luminaire' || id === 'speaker') ||
			housingSize === 'OlivioPiccolo' && (id === 'luminaire' || id === 'speaker')
		);
	}

	colourTemperatureCanBeSelected(state, id) {
		return (id !== 'custom')
			&&
			!(selectOlivioReflectorColour(state) === 'Gold' && id === '3000K') &&
			!(selectOlivioReflectorColour(state) === 'Gold' && id === '4000K');
	}

	lightDistributionCanBeSelected(state, id) {
		return (id !== 'custom');
	}

	accessoriesCanBeSelected(state, id) {
		return (id !== 'custom');
	}

	allHeadDecision(valueId, spcChoice, value) {
		// console.log('allHeadDecision', valueId, spcChoice, value);
		// set for all heads
		if (valueId === 'root::LE_ColourTemperature_1') {
			const heads = Array.from({length: 8}, (_, i) => (value));
			store.dispatch(setOlivioHeadsData({heads: heads}));

			if (value.colourTemperature === 'RGBW') {
				// make sure RGBW can be set
				const headAttributes = [
					{id: 'root::LE_ColourTemperature_1', value: value.colourTemperature},
					{id: 'root::LE_LightDistribution_1', value: 'rotationalSymmetricMediumRGBW'},
					{id: 'root::LE_ReflectorColour_1', value: 'Silver'},
					{id: 'root::LE_Accessories_1', value: 'None'},
				];
				store.dispatch(setOlivioHeadsData({heads:  Array.from({length: 8}, (_, i) => ({lightDistribution: 'rotationalSymmetricMediumRGBW', accessories: 'None', reflectorColour: 'Silver'}))}));
				const spcAttributes = headAttributes.map((attribute) => spcExpliciteChoiceDecision(attribute.id, attribute.value)) ?? [];
				this.session.makeManyDecisions(spcAttributes, {type: 'KeepExistingDecisions'});
			} else {
				// make sure other can be set
				const headAttributes = [
					{id: 'root::LE_ColourTemperature_1', value: value.colourTemperature},
					{id: 'root::LE_LightDistribution_1', value: 'rotationalSymmetricMedium40'},
				];
				store.dispatch(setOlivioHeadsData({heads:  Array.from({length: 8}, (_, i) => ({lightDistribution: 'rotationalSymmetricMedium40'}))}));
				const spcAttributes = headAttributes.map((attribute) => spcExpliciteChoiceDecision(attribute.id, attribute.value)) ?? [];
				this.session.makeManyDecisions(spcAttributes, {type: 'KeepExistingDecisions'});
			}
		} else {
			const heads = Array.from({length: 8}, (_, i) => (value));
			store.dispatch(setOlivioHeadsData({heads: heads}));

			// update default spc
			this.session.makeDecision({
				type: AttributeType.Choice, attributeId: stringToAttributeId(valueId),
				choiceValueId: spcChoice, state: ChoiceValueDecisionState.Included
			});
		}
	}

	getAllHeadSettings() {
		return html`
			<div class="settings active">
				<div class="box">
					${when(!selectOlivioNumberOfHeads(this.state) > 1, () => html`
						<div class="info">
							${msg('This settings will be applied to all heads.')}
						</div>
					`)}
					${when(!selectOlivioDesignIsBollard(this.state), () => html`
						<list-select
							.title=${{de: 'Kopfgröße', en: 'head size', fr: 'taille de la tête'}}
							.options=${spcValueSelectorFactory('HS_HousingSize')(this.state).concat(this.customOption)}
							.callback=${(value) => this.allHeadDecision('root::HS_HousingSize', value, {housingSize: value})}
							.selected=${selectOlivioHousingSizeInclCustom}
							.canBeSelected=${this.housingSizeCanBeSelected}
							.isSatisfied=${() => true}>
						</list-select>
					`)}
					<spc-attribute .attributeId=${{localId: 'LE_ReflectorColour_1'}} .hideDisabled=${true}></spc-attribute>
					<list-select
						.title=${{
							de: 'Lichtfarbe',
							en: 'Light color',
							fr: 'température de couleur'
						}}
						.options=${spcValueSelectorFactory('LE_ColourTemperature_1', true)(this.state).concat(this.customOption)}
						.callback=${(value) => this.allHeadDecision('root::LE_ColourTemperature_1', value, {colourTemperature: value})}
						.selected=${selectOlivioColourTemperatureInclCustom}
						.canBeSelected=${this.colourTemperatureCanBeSelected}
						.isSatisfied=${() => true}>
					</list-select>
					${when(!selectOlivioDesignIsBollard(this.state), () => html`
						<list-select
							.title=${{
								de: 'Abstrahlcharakteristik',
								en: 'light distribution',
								fr: 'distribution photométrique'
							}}
							.options=${spcValueSelectorFactory('LE_LightDistribution_1')(this.state).concat(this.customOption)}
							.callback=${(value) => this.allHeadDecision('root::LE_LightDistribution_1', value, {lightDistribution: value})}
							.selected=${selectOlivioLightDistributionInclCustom}
							.canBeSelected=${this.lightDistributionCanBeSelected}
							.isSatisfied=${() => true}>
						</list-select>
						<list-select
							.title=${{
								de: 'Lichttechnisches Zubehör',
								en: 'Light-technical accoessories',
								fr: 'Accessoires d\'éclairage'
							}}
							.options=${spcValueSelectorFactory('LE_Accessories_1')(this.state)}
							.callback=${(value) => this.allHeadDecision('root::LE_Accessories_1', value, {accessories: value})}
							.selected=${selectOlivioAccessoriesInclCustom}
							.canBeSelected=${this.accessoriesCanBeSelected}
							.isSatisfied=${() => true}>
						</list-select>
					`)}
					<spc-attribute .attributeId=${{localId: 'LE_DimmingLevel_1'}} .hideDisabled=${true}></spc-attribute>
				</div>
			</div>
		`;
	}

	getHeadSettings(instance) {
		const selectHeight = generateOlivioHeadHeightSelector(instance);
		const selectAngle = generateOlivioHeadAngleSelector(instance);
		const selectHousingSize = generateOlivioHeadHousingSizeSelector(instance);
		const selectColourTemperature = generateOlivioHeadColourTemperatureSelector(instance);
		const selectLightDistribution = generateOlivioHeadLightDistributionSelector(instance);
		const selectType = generateOlivioHeadTypeSelector(instance);
		const selectGoboAperture = generateOlivioHeadGoboApertureSelector(instance);

		return html`
			<div class="settings ${classMap({active: this.selectedHead === instance})}">
				${when(selectOlivioDesignIsSistema(this.state) || selectOlivioDesignIsFloracion(this.state), () => html`
					<div class="box">
						<numeric-range
							min="${this.minHeadHeight}" max="${this.maxHeadHeight}" step="0.1" digits="2"
							unit="m"
							.title=${{de: 'Höhe', en: 'Height', fr: 'hauteurs'}}
							.callback=${(value) => store.dispatch(setOlivioHeadHeight({instance: instance, value: value}))}
							.selected=${selectHeight}
							.isSatisfied=${() => true}>
						</numeric-range>
						<numeric-range
							max="360" min="0" step="1" digits="0"
							unit="°"
							.title=${{de: 'Winkel', en: 'Angle', fr: 'Angle'}}
							.callback=${(value) => store.dispatch(setOlivioHeadAngle({instance: instance, value: value}))}
							.selected=${selectAngle}
							.isSatisfied=${() => true}>
						</numeric-range>
					</div>
				`)}
				<div class="box">
					${when(!selectOlivioDesignIsFloracion(this.state), () => html`
						<list-select
							.title=${{
								de: 'Kopfgröße',
								en: 'head size',
								fr: 'taille de la tête'
							}}
							.options=${olivioHeadHousingSizeOptions}
							.callback=${(value) => store.dispatch(setOlivioHeadHousingSize({instance: instance, value: value}))}
							.selected=${selectHousingSize}
							.canBeSelected=${this.housingSizeCanBeSelected}
							.isSatisfied=${() => true}>
						</list-select>
					`)}

					<list-select
						.title=${{
							de: 'Typ',
							en: 'Type',
							fr: 'type'
						}}
						.options=${olivioHeadTypeOptions}
						.callback=${(value) => store.dispatch(setOlivioHeadType({instance: instance, value: value}))}
						.selected=${selectType}
						.canBeSelected=${(state, id) => this.typeCanBeSelected(state, id, selectHousingSize(state))}
						.isSatisfied=${() => true}>
					</list-select>
				</div>

				${when(selectType(this.state) === 'luminaire', () => html`
					<div class="box">
						<list-select
							.title=${{
								de: 'Lichtfarbe',
								en: 'Light color',
								fr: 'température de couleur'
							}}
							.options=${spcValueSelectorFactory('LE_ColourTemperature_1')(this.state)}
							.callback=${(value) => store.dispatch(setOlivioHeadColourTemperature({instance: instance, value: value}))}
							.selected=${selectColourTemperature}
							.canBeSelected=${() => true}
							.isSatisfied=${() => true}>
						</list-select>
						${when(!selectOlivioDesignIsBollard(this.state), () => html`
							<list-select
								.title=${{
									de: 'Abstrahlcharakteristik',
									en: 'Light distribution',
									fr: 'distribution photométrique'
								}}
								.options=${spcValueSelectorFactory('LE_LightDistribution_1')(this.state)}
								.callback=${(value) => store.dispatch(setOlivioHeadLightDistribution({instance: instance, value: value}))}
								.selected=${selectLightDistribution}
								.canBeSelected=${() => true}
								.isSatisfied=${() => true}>
							</list-select>
						`)}
					</div>
				`)}

				${when(selectType(this.state) === 'gobo', () => html`
					<div class="box">
						<list-select
							.title=${{
								de: 'Gobo Brennweite',
								en: 'Gobo Focal Length',
								fr: 'Gobo distance focale'
							}}
							.options=${olivioHeadGoboApertureOptions}
							.callback=${(value) => store.dispatch(setOlivioHeadGoboAperture({instance: instance, value: value}))}
							.selected=${selectGoboAperture}
							.canBeSelected=${() => true}
							.isSatisfied=${() => true}>
						</list-select>
					</div>
				`)}
			</div>
		`;
	}

	render() {
		return html`
			${when(this.numberOfHeads > 1, () => html`
				<div class="box">
					<div>
						<div class="title">${msg('Selected Head')}</div>
						<div class="tabs">
							<button class="${classMap({selected: this.selectedHead === null})}" @click="${() => this.setSelected(null)}">${msg('all')}
							</button>
							${map(range(this.numberOfHeads), (i) => html`
								<button class="${classMap({
									selected: this.selectedHead === i,
									different: generateOlivioHeadIsDifferentFromBase(i)(this.state),
									collision: this.hasCollisions(i)
								})}"
										@click="${() => this.setSelected(i)}">${i + 1}
								</button>`)}
						</div>
					</div>
				</div>
			`)}
			${when(this.selectedHead === null || this.numberOfHeads === 1,
				() => this.getAllHeadSettings(),
				() => map(range(8), (i) => this.getHeadSettings(i)))}
		`;
	}
}
